import { z } from 'zod';

/**
 * What the API serialises, parsed rather than cast — this is a network
 * boundary, and a response that drifted should fail here instead of rendering
 * as `undefined` three components deep.
 */

/**
 * `slug` is optional because upstream's entity declares it optional: tenants
 * created before it existed have none. Requiring it here would crash the whole
 * listing on one old row, which is exactly the kind of failure a boundary
 * schema exists to prevent rather than cause.
 */
export const ClientSummary = z.object({
    tenantId: z.string(),
    slug: z.string().optional(),
    displayName: z.string(),
    orgId: z.string().nullable(),
    subdomain: z.string().nullable(),
    isSandbox: z.boolean().nullable(),
    status: z.string(),
    createdAt: z.string(),
    /**
     * Whether anyone can sign into this client, per identity-api's directory.
     *
     * Three states: `true` (it has people), `false` (it has none), and
     * **absent** — the directory could not be asked. The third is not a
     * formality: rendering it as `false` would mark healthy clients broken on
     * a network blip and send an operator to fix what is not wrong.
     */
    hasOwner: z.boolean().optional(),
});
export type ClientSummary = z.infer<typeof ClientSummary>;

export const ClientListResponse = z.object({ clients: z.array(ClientSummary) });

/** One of a client's people, as identity-api's directory reports them. */
export const ClientPerson = z.object({
    userId: z.string(),
    email: z.string(),
    status: z.string(),
    createdAt: z.string(),
});
export type ClientPerson = z.infer<typeof ClientPerson>;

/**
 * The detail carries the people themselves, not just whether there are any.
 *
 * `people` is absent — never `[]` — when the directory could not be asked.
 * An empty array is upstream saying "nobody"; the two must stay apart.
 *
 * What is NOT here is the login handle. A federation handle lives in the access
 * store as `USERNAME#<handle>`, not on the directory row, so this answers who
 * the client's people are and how to reach them, not what each of them types
 * to sign in.
 */
export const ClientDetail = ClientSummary.extend({
    people: z.array(ClientPerson).optional(),
});
export type ClientDetail = z.infer<typeof ClientDetail>;

/** `null` is a real answer: no client with that id **in this environment**. */
export const ClientDetailResponse = z.object({ client: ClientDetail.nullable() });

/**
 * The owner, as the creation answers once.
 *
 * `temporaryPassword` can be an empty string, and that is a real outcome rather
 * than a missing field: a request that completed a previously half-finished
 * provisioning has no fresh password to hand back — it was delivered on the
 * attempt that created the credential. The screen has to say so instead of
 * rendering a blank.
 */
export const ProvisionedOwner = z.object({
    userId: z.string(),
    username: z.string(),
    email: z.string(),
    temporaryPassword: z.string(),
});
export type ProvisionedOwner = z.infer<typeof ProvisionedOwner>;

/**
 * Deliberately NOT mirrored the same way: upstream constrains a username to
 * what Cognito actually rejects — no whitespace, no control characters, 128
 * characters — and its own docblock says the customer decides what a handle
 * looks like. Two earlier versions of that field invented a character set and
 * both were wrong. Anything stricter here would refuse handles the platform
 * accepts.
 */
export const USERNAME_PATTERN = /^[^\s\p{C}]+$/u;
export const USERNAME_MAX = 128;
